import { MedplumClient, ResourceArray } from "@medplum/core";
import { Condition, Identifier, Procedure } from "@medplum/fhirtypes";

import { MEDPLUM_SEARCH_PAGE_MAX_COUNT } from "@/encounters/constants/medplum-search";
import {
  EncounterProceduresScope,
  PROCEDURES_ENTERED_IN_ERROR,
  ProceduresPickerCategoryCodes,
  ProceduresPickerSystems,
} from "@/encounters/constants/procedures";
import { joinReferenceString, joinTokenString } from "@/utils/medplum";
import { collectResourcesFromNextPages } from "@/utils/pagination";

import { ProcedureOwnership, ProceduresWriteScope } from "../types/procedures";
import {
  buildEncounterProceduresBundle,
  buildProceduresBundleContext,
  EncounterProceduresData,
} from "../utils/procedures";
import { ProceduresPickerFormType } from "../zod-schemas/procedures";

type GetEncounterProceduresParams = {
  medplum: MedplumClient;
  encounterId: string;
  clientId: string;
  /** Which side of the encounter's picker-managed resources to read. */
  scope: EncounterProceduresScope;
  /**
   * Whether to keep the resources a removal retired instead of deleting. Left out by default: every
   * section that shows the encounter shows what it holds now. The amendment diff asks for them,
   * because a removal is one of the changes it has to show.
   */
  includeRetired?: boolean;
};

type SaveEncounterProceduresParams = {
  medplum: MedplumClient;
  formValues: ProceduresPickerFormType;
  initialValues: ProceduresPickerFormType;
  clientId: string;
  encounterId: string;
  isBiller: boolean;
  /**
   * Which side of the encounter this write owns. It decides both what the write reconciles against
   * and what the resources are categorised as, so the two can never come apart.
   */
  scope: ProceduresWriteScope;
  /**
   * How this write identifies the procedures it owns, for a caller that owns them one at a time
   * rather than one per code. See {@link ProcedureOwnership}, and
   * `saveBillerSectionChargeEdit` for the row edit that needs it.
   */
  ownership?: ProcedureOwnership;
};

type GetOwnedProceduresParams = {
  medplum: MedplumClient;
  encounterId: string;
  /** The owner key the procedures are stamped with. */
  identifier: Identifier;
};

const ENCOUNTER_CREATED_CATEGORY = joinTokenString(
  ProceduresPickerSystems.EncounterCodeOrigin,
  ProceduresPickerCategoryCodes.EncounterCreated,
)!;

const ENCOUNTER_DIAGNOSIS_CATEGORY = joinTokenString(
  ProceduresPickerSystems.ConditionCategory,
  ProceduresPickerCategoryCodes.EncounterDiagnosis,
)!;

const NON_CLINICAL_DATA_CATEGORY = joinTokenString(
  ProceduresPickerSystems.NonClinicalData,
  ProceduresPickerCategoryCodes.NonClinicalData,
)!;

/**
 * The category filter that narrows a read to one side of the encounter.
 *
 * `Clinical` excludes the biller section's resources rather than requiring a marking of its own,
 * because everything recorded before the biller section existed carries no marking at all and is
 * clinical.
 */
const toScopeFilters = (scope: EncounterProceduresScope): string[][] => {
  switch (scope) {
    case EncounterProceduresScope.Clinical:
      return [["category:not", NON_CLINICAL_DATA_CATEGORY]];
    case EncounterProceduresScope.NonClinical:
      return [["category", NON_CLINICAL_DATA_CATEGORY]];
    case EncounterProceduresScope.All:
      return [];
  }
};

// Repeated `category` parameters are what the scope filters need, and a plain object cannot carry the
// same key twice, so both searches are built as parameter pairs.
const buildProcedureQuery = (
  patientReference: string,
  encounterReference: string,
  scope: EncounterProceduresScope,
  includeRetired: boolean,
) => [
  ["subject", patientReference],
  ["encounter", encounterReference],
  ["category", ENCOUNTER_CREATED_CATEGORY],
  ["_count", String(MEDPLUM_SEARCH_PAGE_MAX_COUNT)],
  ...(includeRetired ? [] : [["status:not", PROCEDURES_ENTERED_IN_ERROR]]),
  ...toScopeFilters(scope),
];

const buildConditionQuery = (
  patientReference: string,
  encounterReference: string,
  scope: EncounterProceduresScope,
  includeRetired: boolean,
) => [
  ["subject", patientReference],
  ["encounter", encounterReference],
  ["category", ENCOUNTER_DIAGNOSIS_CATEGORY],
  ["_count", String(MEDPLUM_SEARCH_PAGE_MAX_COUNT)],
  ...(includeRetired ? [] : [["verification-status:not", PROCEDURES_ENTERED_IN_ERROR]]),
  ...toScopeFilters(scope),
];

/**
 * Runs one scoped search and follows its `next` pages, so an encounter with more resources than fit
 * on a page is not read short.
 *
 * A failed page read throws instead of returning what was read so far. Every caller compares the
 * result against the encounter as it believes it stands, so a short read either hides resources from
 * a section or reconciles a write against resources it cannot see.
 */
const searchAllPages = async <T extends Procedure | Condition>(
  medplum: MedplumClient,
  resourceType: T["resourceType"],
  query: string[][],
): Promise<T[]> => {
  const firstPage = (await medplum.searchResources(resourceType, query, {
    cache: "no-cache",
  })) as unknown as ResourceArray<T>;

  const nextPages = await collectResourcesFromNextPages<T>(
    medplum,
    firstPage.bundle,
    resourceType,
    {
      throwOnPageError: true,
    },
  );

  return [...firstPage, ...nextPages];
};

// Reads the encounter's picker-managed Procedures and Conditions, the active ones unless the caller
// asks for the retired ones too.
//
// Both searches bypass the Medplum client's request cache: writes go out as transaction bundles, and
// executeBatch does not invalidate cached searches the way createResource and updateResource do. A
// cached read would hand back the encounter as it looked before the write.
export const getEncounterProceduresAndConditions = async ({
  medplum,
  encounterId,
  clientId,
  scope,
  includeRetired = false,
}: GetEncounterProceduresParams): Promise<EncounterProceduresData> => {
  const encounterReference = joinReferenceString("Encounter", encounterId);
  const patientReference = joinReferenceString("Patient", clientId);

  const [procedures, conditions] = await Promise.all([
    searchAllPages<Procedure>(
      medplum,
      "Procedure",
      buildProcedureQuery(patientReference, encounterReference, scope, includeRetired),
    ),
    searchAllPages<Condition>(
      medplum,
      "Condition",
      buildConditionQuery(patientReference, encounterReference, scope, includeRetired),
    ),
  ]);

  return { procedures, conditions };
};

/**
 * The encounter's live procedures carrying one owner key.
 *
 * A caller that owns its procedures per row writes them through the same reconciliation as everyone
 * else, and then has to find the one it just wrote. Reading by the key rather than by the code is
 * the whole point: the code can resolve to more than one procedure on the same encounter, the key
 * cannot.
 *
 * Retired procedures are left out, so a key whose procedure was retired by an earlier save reads as
 * unclaimed and the next write creates a live one under it.
 *
 * Bypasses the request cache for the same reason the scoped read does: the write went out as a
 * transaction bundle, which leaves cached searches untouched.
 */
export const getOwnedEncounterProcedures = ({
  medplum,
  encounterId,
  identifier,
}: GetOwnedProceduresParams): Promise<Procedure[]> =>
  medplum.searchResources(
    "Procedure",
    [
      ["encounter", joinReferenceString("Encounter", encounterId)],
      ["identifier", joinTokenString(identifier.system, identifier.value)!],
      ["status:not", PROCEDURES_ENTERED_IN_ERROR],
    ],
    { cache: "no-cache" },
  );

// Persists the picker's selections directly as Condition and Procedure resources in a single
// transaction, reconciling against what already exists on the encounter (see
// buildEncounterProceduresBundle). Reading the encounter back is the caller's job, so the tab has a
// single place its data comes from.
//
// Reconciliation matches resources by code, so it is scoped to the side of the encounter the write
// owns: a code entered in the biller section and a code entered in the procedures tab are two
// separate records of two separate decisions, and neither write may reshape the other's.
//
// Within one side, a caller whose codes are not unique passes `ownership` and the code match is
// narrowed to the procedures carrying its key. The biller section's row edits do that, because two
// of its rows can be billed under the same code.
export const saveEncounterProceduresAndConditions = async ({
  medplum,
  formValues,
  initialValues,
  clientId,
  encounterId,
  isBiller,
  scope,
  ownership,
}: SaveEncounterProceduresParams): Promise<void> => {
  const { procedures: existingProcedures, conditions: existingConditions } =
    await getEncounterProceduresAndConditions({ medplum, encounterId, clientId, scope });

  const entries = buildEncounterProceduresBundle({
    formValues,
    initialValues,
    existingProcedures,
    existingConditions,
    ownership,
    ...buildProceduresBundleContext({
      profile: medplum.getProfile(),
      clientId,
      encounterId,
      isBiller,
      scope,
    }),
  });

  if (!entries.length) return;

  await medplum.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry: entries,
  });
};
